import _ from 'underscore';
import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { composeObjectPath } from '@culture/common-helpers/params';
import { instituteTypeTitlesHash } from '@culture/utils/helpers/institutes';

import {
	catalogsActions,
	catalogsOperations,
	catalogsSelectors
} from 'ducks/catalogs';
import CatalogPropTypes from 'utils/propTypes/catalogs';

import Catalog from '../components/Catalog';

const CatalogContainer = ({ pagination, urlParams, streamingPlatforms }) => {
	const dispatch = useDispatch();
	const filters = useSelector(catalogsSelectors.selectFilterValues);
	const { push, asPath, pathname } = useRouter();

	useEffect(() => {
		dispatch(
			catalogsOperations.getFilterValues(
				'catalogFilters/streamingPlatforms',
				urlParams
			)
		);
	}, [asPath]);

	useEffect(() => () => dispatch(catalogsActions.resetFilterValues()), []);

	const handleFilterChange = useCallback(
		(name, value) => {
			const currentValue = urlParams[name] || '';

			if (value !== currentValue) {
				push({
					pathname,
					query: composeObjectPath({
						...urlParams,
						[`${name}`]: value,
						page: null
					})
				});
			}
		},
		[urlParams]
	);

	const handleFilterReset = useCallback(() => {
		push({ pathname, query: _(urlParams).pick('sort') });
	}, [urlParams]);

	const filterItems = useMemo(() => {
		const types = filters.types || [];
		const typeValues = types.map((type) => ({
			title: instituteTypeTitlesHash[type],
			name: type
		}));

		return [
			{
				title: 'Тип учреждения',
				ariaLabel: 'Выбор типа учреждения',
				values: typeValues,
				key: 'type',
				icon: 'catalogInstitute',
				type: 'select'
			},
			{
				title: 'Местоположение',
				values: filters.locales || [],
				ariaLabel: 'Выбор местоположения',
				key: 'location',
				icon: 'catalogLocation',
				type: 'select',
				valueField: 'sysName'
			}
		];
	}, [filters, urlParams]);

	return (
		<Catalog
			onFilterChange={handleFilterChange}
			onFilterReset={handleFilterReset}
			filters={filterItems}
			filtersLoaded={!_.isEmpty(filters)}
			pagination={pagination}
			urlParams={urlParams}
			streamingPlatforms={streamingPlatforms}
		/>
	);
};

CatalogContainer.propTypes = {
	pagination: CatalogPropTypes.pagination.isRequired,
	streamingPlatforms: PropTypes.array.isRequired,
	urlParams: PropTypes.object.isRequired
};

export default CatalogContainer;
